// src/pages/reports/ProjectReports.js

import { useEffect, useState } from "react";
import { collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useNavigate } from "react-router-dom";
import "../../styles/print.css";

export default function ProjectReports() {
  const navigate  = useNavigate();
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";

  const [projects,  setProjects]  = useState([]);
  const [company,   setCompany]   = useState({});
  // eslint-disable-next-line no-unused-vars
  const [checklist, setChecklist] = useState({}); // projectId → items[]
  const [summary,   setSummary]   = useState({
    total: 0, active: 0, completed: 0, on_hold: 0,
    totalValue: 0, totalRevenue: 0, totalExpenses: 0,
    profitableProjects: 0, lossProjects: 0,
  });
  const [loading,   setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard"); // dashboard | financial | progress
  const [filter,    setFilter]    = useState("all");

  useEffect(() => { if (companyId) fetchAll(); }, [companyId]); // eslint-disable-line

  const fetchAll = async () => {
    try {
      setLoading(true);

      // Fetch company details
      const compSnap = await getDoc(doc(db, "companies", companyId));
      if (compSnap.exists()) setCompany(compSnap.data());

      // Fetch all in parallel
      const [projSnap, checkSnap, invSnap, expSnap, transSnap, stagesSnap] = await Promise.all([
  getDocs(query(collection(db, "projects"),
    where("company_id", "==", companyId))),
  getDocs(query(collection(db, "project_checklist_items"),
    where("company_id", "==", companyId))),
  getDocs(query(collection(db, "invoices"),
    where("company_id", "==", companyId))),
  getDocs(query(collection(db, "financial_transactions"),
    where("company_id", "==", companyId), where("type", "==", "expense"))),
  getDocs(query(collection(db, "financial_transactions"),
    where("company_id", "==", companyId), where("type", "==", "transport"))),
  getDocs(query(collection(db, "project_material_stages"),
    where("company_id", "==", companyId))),
]);

      // Build checklist map — projectId → items[]
      const checkMap = {};
      checkSnap.forEach(d => {
        const item = { id: d.id, ...d.data() };
        if (!checkMap[item.project_id]) checkMap[item.project_id] = [];
        checkMap[item.project_id].push(item);
      });

      // Build revenue map
      const revenueMap = {};
      invSnap.forEach(d => {
        const data = d.data();
        if (!data.project_id) return;
        revenueMap[data.project_id] = (revenueMap[data.project_id] || 0) + Number(data.total_amount || 0);
      });

      // Build material cost map — project_material_stages se issued_amount
const materialCostMap = {};
stagesSnap.forEach(d => {
  const stage = d.data();
  if (!stage.project_id) return;
  const stageCost = (stage.material_budget || []).reduce(
    (sum, item) => sum + Number(item.issued_amount || 0), 0
  );
  materialCostMap[stage.project_id] = (materialCostMap[stage.project_id] || 0) + stageCost;
});

// Build expense map
const expenseMap = {};
[...expSnap.docs, ...transSnap.docs].forEach(d => {
        const data = d.data();
        const pid  = data.project_id || data.site_id;
        if (!pid) return;
        expenseMap[pid] = (expenseMap[pid] || 0) + Number(data.amount || 0);
      });

      // Build project list
      let totalValue = 0, totalRevenue = 0, totalExpenses = 0;
      let profitableProjects = 0, lossProjects = 0;

      const projectList = projSnap.docs.map(d => {
        const project      = { id: d.id, ...d.data() };
const revenue      = revenueMap[project.id]     || 0;
const expenses     = expenseMap[project.id]     || 0;
const materialCost = materialCostMap[project.id] || 0;
// yeh line delete karo — upar already totalExpenses += expenses + materialCost; hai
const profit       = revenue - totalExpenses;
        const items    = checkMap[project.id]    || [];
        const total    = items.length;
        const done     = items.filter(i => i.status === "completed").length;
        const na       = items.filter(i => i.status === "n/a").length;
        const applicable = total - na;
        const pct      = applicable > 0 ? Math.round((done / applicable) * 100) : 0;
        const milestones = items.filter(i => i.is_milestone);
        const pendingM   = milestones.filter(i => i.status !== "completed").length;

        totalValue    += Number(project.total_value || 0);
        totalRevenue  += revenue;
        totalExpenses += expenses + materialCost;
        if (profit > 0) profitableProjects++;
        else if (profit < 0) lossProjects++;

        return {
  ...project, revenue, expenses: totalExpenses, material_cost: materialCost, profit,
          checklist_total: total, checklist_done: done,
          checklist_pct: pct, pending_milestones: pendingM,
        };
      }).sort((a, b) => {
        const aT = a.created_at?.seconds || 0;
        const bT = b.created_at?.seconds || 0;
        return bT - aT;
      });

      setProjects(projectList);
      setChecklist(checkMap);
      setSummary({
        total:     projectList.length,
        active:    projectList.filter(p => p.status === "active").length,
        completed: projectList.filter(p => p.status === "completed").length,
        on_hold:   projectList.filter(p => p.status === "on_hold").length,
        totalValue, totalRevenue, totalExpenses,
        totalProfit: totalRevenue - totalExpenses,
        profitableProjects, lossProjects,
      });
    } catch (err) {
      console.error("ProjectReports error:", err);
    } finally { setLoading(false); }
  };

  const filtered = projects.filter(p => {
    if (filter === "active")    return p.status === "active";
    if (filter === "completed") return p.status === "completed";
    if (filter === "on_hold")   return p.status === "on_hold";
    if (filter === "profit")    return p.profit > 0;
    if (filter === "loss")      return p.profit < 0;
    return true;
  });

  const STATUS_COLORS = {
    active:    "bg-green-100 text-green-700",
    on_hold:   "bg-yellow-100 text-yellow-700",
    completed: "bg-blue-100 text-blue-700",
    cancelled: "bg-red-100 text-red-700",
  };

  if (loading) return <div className="p-6 text-gray-500">Loading project reports...</div>;

  return (
   <div className="p-4 md:p-6 space-y-5 max-w-7xl mx-auto print-area">

      {/* PRINT HEADER — only on print */}
      <div className="hidden print:block mb-4 border-b-2 border-gray-800 pb-3">
        <div className="flex justify-between">
          <div>
            <h1 className="text-xl font-bold">{company.company_name || "ZENO HVAC ERP"}</h1>
            <p className="text-sm text-gray-600">{company.company_address || ""}</p>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold">PROJECT REPORT</div>
            <div className="text-xs text-gray-500">
              Generated: {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
            </div>
          </div>
        </div>
      </div>

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Project Reports</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {summary.total} projects · ₹{summary.totalValue.toLocaleString("en-IN")} pipeline
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => window.print()}
            className="px-4 py-2 bg-gray-700 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 no-print">
            🖨️ Print Report
          </button>
          <button onClick={() => navigate("/projects/create")}
            className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 no-print">
            + New Project
          </button>
        </div>
      </div>

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total",      val: summary.total,     color: "bg-gray-800",    sub: "projects" },
          { label: "Active",     val: summary.active,    color: "bg-green-600",   sub: "running" },
          { label: "Completed",  val: summary.completed, color: "bg-blue-600",    sub: "done" },
          { label: "On Hold",    val: summary.on_hold,   color: "bg-yellow-500",  sub: "paused" },
        ].map(c => (
          <div key={c.label} className={`${c.color} text-white rounded-xl p-4`}>
            <div className="text-2xl font-bold">{c.val}</div>
            <div className="text-sm opacity-80">{c.label}</div>
            <div className="text-xs opacity-60">{c.sub}</div>
          </div>
        ))}
      </div>

      {/* FINANCIAL SUMMARY */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="text-xs text-gray-400 uppercase mb-1">Pipeline Value</div>
          <div className="text-xl font-bold text-gray-800">
            ₹{summary.totalValue.toLocaleString("en-IN")}
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="text-xs text-gray-400 uppercase mb-1">Revenue Billed</div>
          <div className="text-xl font-bold text-green-700">
            ₹{summary.totalRevenue.toLocaleString("en-IN")}
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="text-xs text-gray-400 uppercase mb-1">Expenses</div>
          <div className="text-xl font-bold text-red-600">
            ₹{summary.totalExpenses.toLocaleString("en-IN")}
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="text-xs text-gray-400 uppercase mb-1">Net Profit</div>
          <div className={`text-xl font-bold ${summary.totalProfit >= 0 ? "text-green-700" : "text-red-600"}`}>
            ₹{summary.totalProfit.toLocaleString("en-IN")}
          </div>
        </div>
      </div>

      {/* TABS */}
      <div className="flex gap-2 border-b border-gray-200">
        {[
          { key: "dashboard", label: "📊 Dashboard" },
          { key: "progress",  label: "📋 Progress" },
          { key: "financial", label: "💰 Financial" },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${
              activeTab === t.key
                ? "border-green-600 text-green-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* FILTER */}
      <div className="flex gap-2 flex-wrap">
        {[
          { val: "all",       label: "All" },
          { val: "active",    label: "Active" },
          { val: "completed", label: "Completed" },
          { val: "on_hold",   label: "On Hold" },
          { val: "profit",    label: "🟢 Profitable" },
          { val: "loss",      label: "🔴 Loss" },
        ].map(f => (
          <button key={f.val} onClick={() => setFilter(f.val)}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition ${
              filter === f.val
                ? "bg-gray-800 text-white"
                : "bg-white border border-gray-200 text-gray-600"
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* ── TAB: DASHBOARD ── */}
      {activeTab === "dashboard" && (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map(p => (
            <div key={p.id}
              onClick={() => navigate(`/projects/${p.id}`)}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md cursor-pointer transition">

              <div className="flex justify-between items-start mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-800 truncate">{p.project_name}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{p.client_name}</p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 ml-2 ${
                  STATUS_COLORS[p.status] || "bg-gray-100 text-gray-600"
                }`}>
                  {p.status?.replace("_", " ")}
                </span>
              </div>

              {/* Progress bar */}
              {p.checklist_total > 0 && (
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>Progress</span>
                    <span>{p.checklist_done}/{p.checklist_total} · {p.checklist_pct}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full transition-all"
                      style={{ width: `${p.checklist_pct}%` }} />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <div className="font-bold text-gray-800">
                    ₹{Number(p.total_value || 0).toLocaleString("en-IN")}
                  </div>
                  <div className="text-gray-400">Value</div>
                </div>
                <div className="bg-green-50 rounded-lg p-2 text-center">
                  <div className="font-bold text-green-700">
                    ₹{p.revenue.toLocaleString("en-IN")}
                  </div>
                  <div className="text-gray-400">Billed</div>
                </div>
                <div className={`rounded-lg p-2 text-center ${
                  p.pending_milestones > 0 ? "bg-amber-50" : "bg-blue-50"
                }`}>
                  <div className={`font-bold ${
                    p.pending_milestones > 0 ? "text-amber-700" : "text-blue-700"
                  }`}>
                    {p.pending_milestones}
                  </div>
                  <div className="text-gray-400">Milestones</div>
                </div>
              </div>

            </div>
          ))}
        </div>
      )}

      {/* ── TAB: PROGRESS ── */}
      {activeTab === "progress" && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-3 text-left font-semibold text-gray-600">Project</th>
                <th className="p-3 text-left font-semibold text-gray-600">Client</th>
                <th className="p-3 text-left font-semibold text-gray-600">Progress</th>
                <th className="p-3 text-center font-semibold text-gray-600">Done</th>
                <th className="p-3 text-center font-semibold text-gray-600">Milestones</th>
                <th className="p-3 text-center font-semibold text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(p => (
                <tr key={p.id}
                  onClick={() => navigate(`/projects/${p.id}`)}
                  className="hover:bg-gray-50 cursor-pointer">
                  <td className="p-3 font-medium text-gray-800">{p.project_name}</td>
                  <td className="p-3 text-gray-500 text-xs">{p.client_name}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-100 rounded-full h-2 min-w-[80px]">
                        <div className="bg-green-500 h-2 rounded-full"
                          style={{ width: `${p.checklist_pct}%` }} />
                      </div>
                      <span className="text-xs text-gray-500 w-8">{p.checklist_pct}%</span>
                    </div>
                  </td>
                  <td className="p-3 text-center text-xs">
                    {p.checklist_done}/{p.checklist_total}
                  </td>
                  <td className="p-3 text-center">
                    {p.pending_milestones > 0 ? (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
                        {p.pending_milestones} pending
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                        All done
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      STATUS_COLORS[p.status] || "bg-gray-100 text-gray-600"
                    }`}>
                      {p.status?.replace("_", " ")}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── TAB: FINANCIAL ── */}
      {activeTab === "financial" && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-3 text-left">Project</th>
                  <th className="p-3 text-left">Client</th>
                  <th className="p-3 text-right">Value</th>
                  <th className="p-3 text-right">Billed</th>
                  <th className="p-3 text-right">Fin. Expenses</th>
<th className="p-3 text-right">Material Cost</th>
<th className="p-3 text-right">Profit</th>
                  <th className="p-3 text-right">Margin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(p => (
                  <tr key={p.id}
                    onClick={() => navigate(`/projects/${p.id}`)}
                    className="hover:bg-gray-50 cursor-pointer">
                    <td className="p-3 font-medium">{p.project_name}</td>
                    <td className="p-3 text-gray-500 text-xs">{p.client_name}</td>
                    <td className="p-3 text-right text-gray-600">
                      ₹{Number(p.total_value || 0).toLocaleString("en-IN")}
                    </td>
                    <td className="p-3 text-right text-green-600">
                      ₹{p.revenue.toLocaleString("en-IN")}
                    </td>
                    <td className="p-3 text-right text-red-500">
  ₹{(p.expenses - (p.material_cost || 0)).toLocaleString("en-IN")}
</td>
<td className="p-3 text-right text-orange-500">
  ₹{(p.material_cost || 0).toLocaleString("en-IN")}
</td>
                    <td className={`p-3 text-right font-semibold ${
                      p.profit >= 0 ? "text-green-600" : "text-red-600"
                    }`}>
                      ₹{p.profit.toLocaleString("en-IN")}
                    </td>
                    <td className="p-3 text-right text-xs text-gray-500">
                      {p.revenue > 0
                        ? `${((p.profit / p.revenue) * 100).toFixed(1)}%`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 font-semibold">
                <tr>
                  <td className="p-3" colSpan={2}>Total</td>
                  <td className="p-3 text-right">
                    ₹{summary.totalValue.toLocaleString("en-IN")}
                  </td>
                  <td className="p-3 text-right text-green-600">
                    ₹{summary.totalRevenue.toLocaleString("en-IN")}
                  </td>
                  <td className="p-3 text-right text-red-500">
                    ₹{summary.totalExpenses.toLocaleString("en-IN")}
                  </td>
                  <td className={`p-3 text-right ${
                    summary.totalProfit >= 0 ? "text-green-600" : "text-red-600"
                  }`}>
                    ₹{summary.totalProfit.toLocaleString("en-IN")}
                  </td>
                  <td className="p-3 text-right text-xs">
                    {summary.totalRevenue > 0
                      ? `${((summary.totalProfit / summary.totalRevenue) * 100).toFixed(1)}%`
                      : "—"}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Analytics */}
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h2 className="font-semibold text-gray-700 mb-3">Analytics</h2>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-500">Avg Revenue/Project</span>
                <span className="font-semibold">
                  ₹{summary.total > 0
                    ? Math.round(summary.totalRevenue / summary.total).toLocaleString("en-IN")
                    : 0}
                </span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-500">Avg Profit Margin</span>
                <span className="font-semibold">
                  {summary.totalRevenue > 0
                    ? `${((summary.totalProfit / summary.totalRevenue) * 100).toFixed(1)}%`
                    : "—"}
                </span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-500">Profitable Projects</span>
                <span className="font-semibold text-green-600">
                  {summary.profitableProjects}/{summary.total}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}